import { useEffect, useState } from 'react';
import {
  isOnline, reportNetworkFailure, reportNetworkSuccess, subscribeNetwork,
} from '../cache/useNetworkStatus';

/**
 * connection — a heartbeat to OUR server, feeding the one shared
 * online/offline state in ui/cache/useNetworkStatus.
 *
 * `navigator.onLine` only says the machine has a network; the school's
 * wifi can be up while the internet (or the server) is not. So the app
 * pings /health:
 *   every 20s while connected    (60s while the tab is hidden)
 *   every 3s  while disconnected — so saves waiting in the outbox go
 *                                  out within seconds of the line coming back
 *   at once when the browser reports the network came back
 * Every real API request also reports its own outcome (ui/api.js), so
 * the heartbeat is only the backstop for quiet periods.
 */

const ROOT = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8787';
const TIMEOUT_MS = 6000;

const state = { latency: null, lastOk: null, offlineSince: isOnline() ? null : Date.now() };
const listeners = new Set();
const publish = () => listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });

let timer = null;
let inflight = null;

export function ping() {
  if (inflight) return inflight;
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const kill = setTimeout(() => ctrl?.abort(), TIMEOUT_MS);
  const t0 = performance.now();
  inflight = fetch(`${ROOT}/health`, { cache: 'no-store', signal: ctrl?.signal })
    .then((res) => {
      if (!res.ok) throw new Error(String(res.status));
      state.latency = Math.round(performance.now() - t0);
      state.lastOk = Date.now();
      reportNetworkSuccess();
      return true;
    })
    .catch(() => { reportNetworkFailure(); return false; })
    .finally(() => { clearTimeout(kill); inflight = null; publish(); schedule(); });
  return inflight;
}

function schedule() {
  clearTimeout(timer);
  const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
  const delay = !isOnline() ? 3000 : hidden ? 60000 : 20000;
  timer = setTimeout(ping, delay);
}

/** Resolves once the server is reachable again (immediately if it is). */
export function waitForOnline() {
  if (isOnline()) return Promise.resolve();
  return new Promise((resolve) => {
    const off = subscribeNetwork(() => {
      if (isOnline()) { off(); resolve(); }
    });
  });
}

if (typeof window !== 'undefined') {
  subscribeNetwork(() => {
    if (isOnline()) state.offlineSince = null;
    else if (!state.offlineSince) state.offlineSince = Date.now();
    publish();
    schedule();
  });
  window.addEventListener('online', () => ping());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') ping();
  });
  // First check shortly after load, then the loop runs itself.
  setTimeout(ping, 1500);
}

/**
 * @returns {{ online: boolean, latency: number|null, lastOk: number|null,
 *            offlineSince: number|null }}
 */
export function useConnection() {
  const read = () => ({ online: isOnline(), ...state });
  const [value, setValue] = useState(read);
  useEffect(() => {
    const sync = () => setValue(read());
    listeners.add(sync);
    const off = subscribeNetwork(sync);
    sync();
    return () => { listeners.delete(sync); off(); };
  }, []);
  return value;
}
